import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Image as ImageIcon, Calendar, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Gallery {
  id: string;
  slug: string;
  title: string;
  event_date: string | null;
  is_published: boolean;
  is_listed: boolean;
  allow_downloads: boolean;
  cover_image_url: string | null;
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const AdminGalleries = () => {
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    title: "",
    slug: "",
    description: "",
    event_date: "",
    password: "",
    is_published: true,
    is_listed: false,
    allow_downloads: true,
  });

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("galleries")
      .select("id, slug, title, event_date, is_published, is_listed, allow_downloads, cover_image_url")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setGalleries(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!form.title || !form.password || form.password.length < 4) {
      toast.error("Titre et mot de passe (≥4 car.) requis");
      return;
    }
    setCreating(true);
    try {
      // Hash password via edge function
      const { data: hashRes, error: hashErr } = await supabase.functions.invoke(
        "gallery-hash-password",
        { body: { password: form.password } },
      );
      if (hashErr || hashRes?.error) throw new Error(hashErr?.message || hashRes?.error);

      const slug = form.slug || slugify(form.title);
      const { error } = await supabase.from("galleries").insert({
        slug,
        title: form.title,
        description: form.description || null,
        event_date: form.event_date || null,
        password_hash: hashRes.hash,
        is_published: form.is_published,
        is_listed: form.is_listed,
        allow_downloads: form.allow_downloads,
      });
      if (error) throw error;

      toast.success("Galerie créée");
      setOpen(false);
      setForm({
        title: "",
        slug: "",
        description: "",
        event_date: "",
        password: "",
        is_published: true,
        is_listed: false,
        allow_downloads: true,
      });
      load();
    } catch (e: any) {
      toast.error(e.message || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Galeries photo</h1>
          <p className="text-muted-foreground">Gérez les galeries événementielles protégées par mot de passe.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" /> Nouvelle galerie
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Créer une galerie</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Titre</Label>
                <Input
                  value={form.title}
                  onChange={(e) =>
                    setForm({ ...form, title: e.target.value, slug: slugify(e.target.value) })
                  }
                  placeholder="Mariage Julie & Marc"
                />
              </div>
              <div>
                <Label>Slug (URL)</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} />
              </div>
              <div>
                <Label>Date de l'événement</Label>
                <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
              </div>
              <div>
                <Label>Description (optionnel)</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <Label>Mot de passe</Label>
                <Input
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="À transmettre au client"
                />
              </div>
              <div className="space-y-3 pt-2 border-t">
                <div className="flex items-center justify-between">
                  <Label htmlFor="pub">Publiée</Label>
                  <Switch id="pub" checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="list">Listée publiquement sur /galeries</Label>
                  <Switch id="list" checked={form.is_listed} onCheckedChange={(v) => setForm({ ...form, is_listed: v })} />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="dl">Téléchargements autorisés</Label>
                  <Switch id="dl" checked={form.allow_downloads} onCheckedChange={(v) => setForm({ ...form, allow_downloads: v })} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
        </div>
      ) : galleries.length === 0 ? (
        <Card className="p-12 text-center">
          <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Aucune galerie. Créez-en une pour commencer.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {galleries.map((g) => (
            <Link key={g.id} to={`/admin/galeries/${g.slug}`}>
              <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-[4/3] bg-muted">
                  {g.cover_image_url ? (
                    <img src={g.cover_image_url} alt={g.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="h-10 w-10 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="p-4 space-y-2">
                  <h3 className="font-semibold">{g.title}</h3>
                  {g.event_date && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {new Date(g.event_date).toLocaleDateString("fr-CA")}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    <Badge variant={g.is_published ? "default" : "secondary"}>
                      {g.is_published ? "Publiée" : "Brouillon"}
                    </Badge>
                    {g.is_listed && <Badge variant="outline">Listée</Badge>}
                    {g.allow_downloads && <Badge variant="outline">DL ✓</Badge>}
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGalleries;
